import { useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import {
    ShoppingBag, CheckCircle2, XCircle, Wifi, WifiOff,
    Save, RefreshCw, ExternalLink, Settings2,
    Users, Zap, ChevronDown, ChevronUp, Info
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminOkLovendo() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);

    // Form state
    const [baseUrl, setBaseUrl] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPass, setAdminPass] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [saving, setSaving] = useState(false);

    // Test state
    const [testResult, setTestResult] = useState(null);
    const [testing, setTesting] = useState(false);

    // Bulk sync state
    const [bulkResult, setBulkResult] = useState(null);
    const [bulking, setBulking] = useState(false);

    const [showCapabilities, setShowCapabilities] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/admin/oklovendo/settings');
            setCfg(data);
            setBaseUrl(data.base_url || 'https://oklovendo.okgo.mx');
            setEnabled(data.enabled);
        } catch (e) {
            const status = e?.response?.status;
            if (status === 401 || status === 403) toast.error('Sesión expirada — vuelve a iniciar sesión');
            else toast.error('Error al cargar configuración OkLovendo');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { base_url: baseUrl, enabled };
            if (adminEmail) payload.admin_email = adminEmail;
            if (adminPass)  payload.admin_pass  = adminPass;
            await api.post('/admin/oklovendo/settings', payload);
            toast.success('Configuración guardada');
            setAdminEmail('');
            setAdminPass('');
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const testConn = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post('/admin/oklovendo/test');
            setTestResult({ ok: data.ok, ...data });
        } catch (e) {
            setTestResult({ ok: false, error: e?.response?.data?.detail || 'Sin respuesta' });
        } finally {
            setTesting(false);
        }
    };

    const bulkSync = async () => {
        if (!window.confirm('¿Sincronizar todos los usuarios activos a OkLovendo? Esto puede tardar varios minutos.')) return;
        setBulking(true);
        setBulkResult(null);
        try {
            const { data } = await api.post('/admin/oklovendo/bulk-sync');
            setBulkResult(data);
            toast.success(`Sync completado: ${data.synced} ok, ${data.failed} fallidos`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Error en bulk sync');
        } finally {
            setBulking(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center h-48 text-muted-foreground">Cargando…</div>
    );

    const isConfigured = cfg?.has_admin_email && cfg?.has_admin_pass;

    return (
        <div className="space-y-6 max-w-3xl">

            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-xl bg-[#dc2626] flex items-center justify-center">
                            <ShoppingBag className="h-5 w-5 text-white" />
                        </div>
                        <h1 className="text-xl font-bold">OkLovendo — Registro automático</h1>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                        Cada usuario nuevo en OkGo se registra automáticamente en OkLovendo.
                    </p>
                </div>
                <a
                    href="https://oklovendo.okgo.mx"
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1 shrink-0"
                >
                    <ExternalLink className="h-3 w-3" /> oklovendo.okgo.mx
                </a>
            </div>

            {/* Status banner */}
            <div className={`rounded-lg border p-4 flex items-center gap-3 ${
                isConfigured && enabled
                    ? 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800'
                    : 'bg-yellow-50 border-yellow-200 dark:bg-yellow-950/30 dark:border-yellow-800'
            }`}>
                {isConfigured && enabled ? (
                    <>
                        <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                        <div>
                            <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                                OkLovendo configurado · Sync activo
                            </p>
                            <p className="text-xs text-green-600 dark:text-green-500">
                                Cuenta admin: {cfg.admin_email_masked} · {cfg.base_url}
                            </p>
                        </div>
                    </>
                ) : (
                    <>
                        <WifiOff className="h-5 w-5 text-yellow-600 shrink-0" />
                        <div>
                            <p className="text-sm font-semibold text-yellow-700 dark:text-yellow-400">
                                {!isConfigured ? 'Credenciales admin pendientes' : 'Sync deshabilitado'}
                            </p>
                            <p className="text-xs text-yellow-600 dark:text-yellow-500">
                                {!isConfigured
                                    ? 'Guarda el email y contraseña de administrador de OkLovendo para activar el sync.'
                                    : 'Activa el sync para que nuevos usuarios se registren en OkLovendo.'}
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* How it works callout */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800 p-4 flex gap-3">
                <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                    <p className="font-semibold">Flujo de registro</p>
                    <ol className="text-xs text-blue-700 dark:text-blue-400 space-y-0.5 list-decimal list-inside">
                        <li>Usuario se registra en OkGo (nickname, nombre, email, teléfono, ubicación)</li>
                        <li>OkGo llama a OkLovendo <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">POST /api/admin/login</code> para obtener un JWT de admin</li>
                        <li>Con ese JWT, llama a <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">POST /api/admin/users</code> y registra al usuario</li>
                        <li>El JWT se cachea en memoria y se renueva automáticamente</li>
                        <li>El usuario obtiene PIN numérico genérico según la configuración de OkLovendo</li>
                    </ol>
                </div>
            </div>

            {/* Settings card */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2 font-semibold text-sm">
                    <Settings2 className="h-4 w-4" /> Credenciales de administrador
                </div>

                <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">
                        URL base de OkLovendo
                    </label>
                    <input
                        value={baseUrl}
                        onChange={e => setBaseUrl(e.target.value)}
                        className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                        placeholder="https://oklovendo.okgo.mx"
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                            Email de admin OkLovendo{' '}
                            {cfg?.has_admin_email && (
                                <span className="text-green-600">(guardado: {cfg.admin_email_masked})</span>
                            )}
                        </label>
                        <input
                            type="email"
                            value={adminEmail}
                            onChange={e => setAdminEmail(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            placeholder={cfg?.has_admin_email ? 'Escribe para cambiar' : 'admin@oklovendo.mx'}
                        />
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">
                            Contraseña de admin OkLovendo{' '}
                            {cfg?.has_admin_pass && (
                                <span className="text-green-600">(guardada ✓)</span>
                            )}
                        </label>
                        <input
                            type="password"
                            value={adminPass}
                            onChange={e => setAdminPass(e.target.value)}
                            className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                            placeholder={cfg?.has_admin_pass ? 'Escribe para cambiar' : '••••••••'}
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="oklovendo-enabled"
                        checked={enabled}
                        onChange={e => setEnabled(e.target.checked)}
                        className="rounded"
                    />
                    <label htmlFor="oklovendo-enabled" className="text-sm">
                        Registro automático habilitado (sincroniza nuevos usuarios al crearse)
                    </label>
                </div>

                <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <button
                        onClick={save}
                        disabled={saving}
                        className="flex items-center gap-1.5 bg-[#dc2626] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#b91c1c] disabled:opacity-50"
                    >
                        <Save className="h-3.5 w-3.5" />
                        {saving ? 'Guardando…' : 'Guardar'}
                    </button>
                    <button
                        onClick={testConn}
                        disabled={testing || !isConfigured}
                        className="flex items-center gap-1.5 border px-4 py-2 rounded-md text-sm font-medium hover:bg-secondary disabled:opacity-50"
                    >
                        {testing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Wifi className="h-3.5 w-3.5" />}
                        {testing ? 'Probando…' : 'Probar conexión'}
                    </button>
                </div>

                {testResult && (
                    <div className={`rounded-md p-3 text-sm flex items-start gap-2 ${
                        testResult.ok
                            ? 'bg-green-50 border border-green-200 text-green-700'
                            : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                        {testResult.ok ? (
                            <>
                                <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                                <div>
                                    <strong>Conexión exitosa</strong>
                                    {' · '}{testResult.latency_ms}ms
                                    {' · '}JWT admin: {testResult.has_token ? '✓ obtenido' : '✗ credenciales incorrectas'}
                                </div>
                            </>
                        ) : (
                            <>
                                <XCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                <div>
                                    <strong>Error:</strong>{' '}
                                    {testResult.has_token === false
                                        ? 'Credenciales incorrectas — verifica email y contraseña de admin OkLovendo'
                                        : (testResult.error || `HTTP ${testResult.http}`)}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Capabilities */}
            <div className="rounded-lg border bg-card p-5 space-y-3">
                <button
                    onClick={() => setShowCapabilities(c => !c)}
                    className="flex items-center justify-between w-full text-sm font-semibold"
                >
                    <span className="flex items-center gap-2">
                        <Zap className="h-4 w-4" /> Capacidades de integración
                    </span>
                    {showCapabilities ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </button>
                {showCapabilities && (
                    <div className="pt-1 space-y-2">
                        {[
                            {
                                label: 'Registro automático al crear usuario',
                                desc: 'Cuando un usuario se crea en OkGo, se registra en OkLovendo con los mismos datos. Ocurre en background — el registro en OkGo responde de inmediato.'
                            },
                            {
                                label: 'Campos sincronizados',
                                desc: 'nickname (obligatorio), full_name, email, phone, location. OkLovendo asigna un PIN numérico según su configuración interna.'
                            },
                            {
                                label: 'JWT con caché automático',
                                desc: 'El token de admin se obtiene una vez y se reutiliza. Se renueva automáticamente al expirar sin interrumpir el servicio.'
                            },
                            {
                                label: 'Usuarios ya existentes',
                                desc: 'Si el usuario ya existe en OkLovendo (nickname duplicado), se registra como éxito — no genera errores falsos.'
                            },
                        ].map(({ label, desc }) => (
                            <div key={label} className="flex items-start gap-2 text-sm">
                                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium">{label}</p>
                                    <p className="text-xs text-muted-foreground">{desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Bulk sync */}
            <div className="rounded-lg border bg-card p-5 space-y-4">
                <div className="flex items-center gap-2 font-semibold text-sm">
                    <Users className="h-4 w-4" /> Sincronización masiva
                </div>
                <p className="text-sm text-muted-foreground">
                    Registra todos los usuarios activos de OkGo en OkLovendo en una sola operación.
                    Los usuarios ya existentes se detectan y se omiten.
                </p>
                <button
                    onClick={bulkSync}
                    disabled={bulking || !isConfigured}
                    className="flex items-center gap-1.5 border px-4 py-2 rounded-md text-sm font-medium hover:bg-secondary disabled:opacity-50"
                >
                    {bulking ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Users className="h-3.5 w-3.5" />}
                    {bulking ? 'Sincronizando…' : 'Sincronizar todos los usuarios'}
                </button>
                {bulkResult && (
                    <div className={`rounded-md p-3 text-sm ${
                        bulkResult.ok ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                        <p className="font-semibold mb-1">
                            {bulkResult.ok ? '✓ Sync completado' : '⚠ Sync con errores'}
                        </p>
                        <div className="flex gap-4 text-xs">
                            <span className="text-green-700">✓ Registrados: {bulkResult.synced}</span>
                            {bulkResult.failed > 0 && <span className="text-red-600">✗ Fallidos: {bulkResult.failed}</span>}
                            {bulkResult.skipped > 0 && <span className="text-muted-foreground">— Omitidos: {bulkResult.skipped}</span>}
                        </div>
                        {bulkResult.errors?.length > 0 && (
                            <div className="mt-2 space-y-0.5">
                                {bulkResult.errors.slice(0, 5).map((e, i) => (
                                    <p key={i} className="text-xs text-red-600 font-mono">
                                        {e.nickname}: {e.error}
                                    </p>
                                ))}
                                {bulkResult.errors.length > 5 && (
                                    <p className="text-xs text-muted-foreground">…y {bulkResult.errors.length - 5} más</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

        </div>
    );
}
